import { useState } from "react";
import { ArrowRight, Users, UserPlus, Headset, TrendingUp, LogOut, Image as ImageIcon } from "lucide-react";

const HR = {
  name: "HR & People Ops",
  title: "Reimagine the Employee Lifecycle with Agentic AI",
  subtitle:
    "Transform how HR teams hire, onboard, support, develop and manage employees with intelligent AI agents that understand workforce context, make decisions, coordinate work and take action across enterprise systems.",
  useCaseName: "Hire-to-Retire",
  useCaseDesc:
    "Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.",
  journey: ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"],
  agentsHeading: "AI Agents at Work Across the Employee Lifecycle",
  agents: [
    { icon: Users, title: "Talent Acquisition", desc: "Evaluate, reason, recommend and coordinate hiring decisions. Assess candidate-role fit, synthesize recruiter and interview feedback, recommend next-best actions and proactively move candidates through the hiring journey." },
    { icon: UserPlus, title: "Employee Onboarding", desc: "Coordinate onboarding across teams and proactively resolve blockers. Plan and orchestrate documentation, background checks, access, assets, payroll, training and stakeholder actions across HR, IT, Finance and business teams." },
    { icon: Headset, title: "HR Service Delivery", desc: "Understand employee needs, interpret policies and execute the right action. Reason over HR policies and employee information, determine the next-best action and execute across enterprise systems." },
    { icon: TrendingUp, title: "Performance & Talent", desc: "Turn workforce signals into intelligent talent decisions and actions. Synthesize goals, performance, skills, aspirations and organizational context to recommend development, mobility, retention and talent actions." },
    { icon: LogOut, title: "Employee Offboarding", desc: "Securely orchestrate every activity required for a compliant employee exit. Coordinate knowledge transfer, access revocation, asset recovery, payroll actions and stakeholder dependencies while validating compliance." },
  ],
};

const FUNCTIONS = [
  { id: "sales", name: "Sales" },
  { id: "marketing", name: "Marketing" },
  { id: "finance", name: "Finance & Accounting" },
  { id: "supplychain", name: "Supply Chain" },
  { id: "cs", name: "Customer Service" },
  { id: "hr", name: "HR & People Ops", data: HR },
];

const Journey = ({ stages, tone = "light" }) => (
  <div className="flex flex-wrap items-center gap-2">
    {stages.map((s, i) => (
      <div key={s} className="flex items-center gap-2">
        <span className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border ${tone === "dark" ? "bg-white/8 border-white/15 text-[#F7F5F0]" : "bg-[#8A5730]/8 border-[#8A5730]/25 text-[#3E2818]"}`}>{s}</span>
        {i < stages.length - 1 && <ArrowRight size={13} className="text-[#8A5730]" />}
      </div>
    ))}
  </div>
);

const Thumb = ({ label = "Demo application" }) => (
  <div className="rounded-xl border border-dashed border-[#8A5730]/35 bg-[#F7F5F0] aspect-[4/3] flex flex-col items-center justify-center text-center px-6">
    <div className="w-10 h-10 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3">
      <ImageIcon size={18} strokeWidth={1.8} />
    </div>
    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730]">{label}</p>
    <p className="text-[12px] text-[#3E2818]/45 mt-1.5">Thumbnail placeholder</p>
  </div>
);

const AgentCard = ({ a }) => (
  <div className="rounded-xl bg-white border border-[#3E2818]/10 p-5 hover:border-[#8A5730]/35 hover:shadow-[0_18px_44px_-28px_rgba(62,40,24,0.4)] transition-all duration-300">
    <div className="w-9 h-9 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3">
      <a.icon size={16} strokeWidth={1.9} />
    </div>
    <p className="font-semibold text-[#3E2818] text-sm">{a.title}</p>
    <p className="text-[12px] text-[#3E2818]/55 mt-1.5 leading-snug">{a.desc}</p>
  </div>
);

const Placeholder = ({ name }) => (
  <div className="rounded-2xl border border-dashed border-[#3E2818]/20 bg-white/50 py-20 text-center">
    <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A5730]">{name}</p>
    <p className="text-sm text-[#3E2818]/50 mt-2">Content coming soon — HR shown as the example layout.</p>
  </div>
);

const SectionHeader = () => (
  <div className="max-w-4xl mb-8">
    <div className="flex items-center gap-3 mb-4">
      <span className="h-px w-10 bg-[#8A5730]" />
      <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A5730]">Solutions by Function</span>
    </div>
    <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#3E2818] leading-[1.08]">Build for Your Core Functions</h2>
    <p className="mt-4 text-base text-[#3E2818]/65 leading-relaxed">Agentic AI solutions tailored to how each business function actually works — from Sales to HR.</p>
  </div>
);

/* ---------------- Option 1 · Top Tabs + Detail ---------------- */
const OptionTabs = () => {
  const [active, setActive] = useState("hr");
  const f = FUNCTIONS.find((x) => x.id === active);
  return (
    <div className="bg-[#F7F5F0] p-6 lg:p-10">
      <SectionHeader />
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#3E2818]/10 pb-4">
        {FUNCTIONS.map((x) => (
          <button key={x.id} onClick={() => setActive(x.id)}
            className={`rounded-full px-4 py-2 text-[13px] font-semibold border transition ${active === x.id ? "bg-[#3E2818] text-[#F7F5F0] border-[#3E2818]" : "bg-white text-[#3E2818]/70 border-[#3E2818]/12 hover:border-[#8A5730]/40"}`}>
            {x.name}
          </button>
        ))}
      </div>
      {!f.data ? <Placeholder name={f.name} /> : (
        <div className="grid lg:grid-cols-[1.35fr_0.65fr] gap-8">
          <div>
            <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{f.data.title}</h3>
            <p className="text-sm text-[#3E2818]/65 mt-3 max-w-2xl leading-relaxed">{f.data.subtitle}</p>
            <div className="mt-6 rounded-xl bg-[#3E2818] text-[#F7F5F0] p-5">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Overall Use Case · {f.data.useCaseName}</span>
              <p className="text-[13px] text-white/75 mt-1.5 mb-4">{f.data.useCaseDesc}</p>
              <Journey stages={f.data.journey} tone="dark" />
            </div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#8A5730] mt-7 mb-3">{f.data.agentsHeading}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {f.data.agents.map((a) => <AgentCard key={a.title} a={a} />)}
            </div>
          </div>
          <div className="lg:sticky lg:top-6 self-start">
            <Thumb />
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Option 2 · Sidebar Navigator ---------------- */
const OptionSidebar = () => {
  const [active, setActive] = useState("hr");
  const f = FUNCTIONS.find((x) => x.id === active);
  return (
    <div className="bg-[#F7F5F0] p-6 lg:p-10">
      <SectionHeader />
      <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6">
        <div className="flex flex-col gap-2">
          {FUNCTIONS.map((x) => (
            <button key={x.id} onClick={() => setActive(x.id)}
              className={`text-left rounded-xl px-4 py-3 border text-sm font-semibold transition flex items-center justify-between group ${active === x.id ? "bg-white border-[#8A5730]/45 text-[#3E2818] shadow-[0_10px_30px_-18px_rgba(62,40,24,0.5)]" : "bg-white/50 border-[#3E2818]/10 text-[#3E2818]/70 hover:border-[#8A5730]/30"}`}>
              {x.name}
              <ArrowRight size={15} className={`text-[#8A5730] transition ${active === x.id ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} />
            </button>
          ))}
        </div>
        {!f.data ? <Placeholder name={f.name} /> : (
          <div className="rounded-2xl bg-white border border-[#3E2818]/10 p-6 lg:p-8">
            <div className="grid lg:grid-cols-[1fr_0.5fr] gap-6 items-start">
              <div>
                <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{f.data.title}</h3>
                <p className="text-sm text-[#3E2818]/65 mt-3 leading-relaxed">{f.data.subtitle}</p>
              </div>
              <Thumb />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-[#F7F5F0] p-4 border border-[#3E2818]/8">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">{f.data.useCaseName}</span>
              <Journey stages={f.data.journey} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
              {f.data.agents.map((a) => <AgentCard key={a.title} a={a} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Option 3 · Journey-Led ---------------- */
const OptionJourney = () => {
  const [active, setActive] = useState("hr");
  const f = FUNCTIONS.find((x) => x.id === active);
  return (
    <div className="bg-[#F7F5F0] p-6 lg:p-10">
      <SectionHeader />
      <div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-[#3E2818]/5 border border-[#3E2818]/10 mb-8">
        {FUNCTIONS.map((x) => (
          <button key={x.id} onClick={() => setActive(x.id)}
            className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition ${active === x.id ? "bg-[#3E2818] text-[#F7F5F0]" : "text-[#3E2818]/60 hover:text-[#3E2818]"}`}>
            {x.name}
          </button>
        ))}
      </div>
      {!f.data ? <Placeholder name={f.name} /> : (
        <>
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-end mb-8">
            <div>
              <h3 className="font-display text-2xl lg:text-4xl font-medium text-[#3E2818] tracking-tight leading-tight">{f.data.title}</h3>
              <p className="text-sm text-[#3E2818]/65 mt-3 max-w-2xl leading-relaxed">{f.data.subtitle}</p>
            </div>
            <div className="max-w-sm w-full"><Thumb /></div>
          </div>
          {/* Journey timeline hero */}
          <div className="rounded-2xl bg-[#3E2818] text-[#F7F5F0] p-6 lg:p-7 mb-8">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Overall Use Case · {f.data.useCaseName}</span>
            <p className="text-sm text-white/75 mt-1.5 mb-5 max-w-2xl">{f.data.useCaseDesc}</p>
            <div className="flex flex-wrap items-center gap-3">
              {f.data.journey.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 rounded-full bg-white/8 border border-white/15 px-4 py-2">
                    <span className="font-mono2 text-[10px] text-[#E8AA68]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[13px] font-semibold">{s}</span>
                  </div>
                  {i < f.data.journey.length - 1 && <ArrowRight size={15} className="text-[#E8AA68]" />}
                </div>
              ))}
            </div>
          </div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#8A5730] mb-3">{f.data.agentsHeading}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {f.data.agents.map((a) => <AgentCard key={a.title} a={a} />)}
          </div>
        </>
      )}
    </div>
  );
};

const CONCEPTS = [
  { id: "tabs", label: "Option 1 · Top Tabs + Detail", comp: OptionTabs, note: "Function tabs across the top; below, a two-column detail with the title/subtitle, a dark 'Hire-to-Retire' journey card, the 5 agent cards, and a sticky demo thumbnail on the right." },
  { id: "sidebar", label: "Option 2 · Sidebar Navigator", comp: OptionSidebar, note: "A vertical list of the 6 functions on the left; the selected function opens as a panel on the right with title, thumbnail, journey strip and a 3-column grid of agents." },
  { id: "journey", label: "Option 3 · Journey-Led", comp: OptionJourney, note: "A segmented function switcher, then a prominent dark 'Hire-to-Retire' journey timeline as the hero, followed by the agent cards and a demo thumbnail." },
];

export default function FunctionsOptions() {
  const [active, setActive] = useState("tabs");
  const c = CONCEPTS.find((x) => x.id === active);
  const Comp = c.comp;
  return (
    <div className="min-h-screen bg-[#161009] text-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-10">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E8AA68]">Section 4 — Layout Options</p>
        <h1 className="font-display text-2xl lg:text-4xl font-medium mt-2 tracking-tight">Build for Your Core Functions — layout</h1>
        <p className="text-sm text-white/55 mt-3 max-w-2xl leading-relaxed">HR is fully populated as the example; the other five functions are placeholder tabs. Toggle the concepts and pick one.</p>
        <div className="flex flex-wrap gap-2 mt-6">
          {CONCEPTS.map((x) => (
            <button key={x.id} data-testid={`fn-concept-${x.id}`} onClick={() => setActive(x.id)}
              className={`rounded-full px-4 py-2 text-[12px] font-semibold border transition ${active === x.id ? "bg-[#E8AA68] text-[#211307] border-[#E8AA68]" : "bg-white/5 text-white/70 border-white/15 hover:border-white/40"}`}>
              {x.label}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-white/45 mt-4 max-w-3xl leading-relaxed">{c.note}</p>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-14 pt-6">
        <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]">
          <Comp />
        </div>
      </div>
    </div>
  );
}
